<template>
  <WfRow
    :selectable="true"
    :expandable="true"
    :status="collectionStatus"
    :is-open="isOpen"
    @open="isOpen = $event"
    @check="handleCollectionCheck"
  >
    <template #title>
      <span :class="$style.label">
        <strong class="text-ellipsis" :title="collection.name">{{ collection.name }}</strong>
        <span :class="$style.quantitiesDescription">({{ quantitiesDescription }})</span>
      </span>
    </template>
    <template #content v-if="modesQuantity > 1">
      <WfRow
        :selectable="true"
        :expandable="true"
        :level="1"
        :status="modesStatus"
        :is-open="isModesOpen"
        @open="isModesOpen = $event"
        @check="handleAllModesCheck"
      >
        <template #title>
          <strong>All modes</strong>
        </template>
        <template #content>
          <WfMode
            v-for="(mode, index) in collection.modes"
            :key="mode.compositeId"
            :mode="mode"
            :is-default="index === 0"
          />
        </template>
      </WfRow>
      <WfRow
        :selectable="true"
        :expandable="true"
        :level="1"
        :status="allVariablesStatus"
        @check="handleAllVariablesCheck"
        :is-open="isVariablesOpen"
        @open="isVariablesOpen = $event"
      >
        <template #title>
          <strong>All variables</strong>
        </template>
        <template #content>
          <WfVariableGroup v-for="group in groups" :key="group.id" :group="group" :level="2" />
        </template>
      </WfRow>
    </template>
    <template #content v-else-if="groupsQuantity > 1">
      <WfVariableGroup v-for="group in groups" :key="group.id" :group="group" :level="1" />
    </template>
    <template #content v-else>
      <WfVariable
        v-for="variable in groups[0].variables"
        :key="variable.id"
        :variable="variable"
        :level="1"
      />
    </template>
  </WfRow>
</template>

<style lang="scss" module>
.label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.quantitiesDescription {
  flex-shrink: 0;
}
</style>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import WfRow from './row/WfRow.vue';
import WfVariableGroup from './WfVariableGroup.vue';
import WfVariable from './WfVariable.vue';
import WfMode from './WfMode.vue';

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
});

const store = useStore();

const isOpen = ref(false);
const isModesOpen = ref(false);
const isVariablesOpen = ref(false);

const modesQuantity = computed(() => props.collection.modes.length);

const modesStatus = computed(() =>
  store.getters['variable/getModesStatus'](props.collection.modes.map((mode) => mode.compositeId))
);

const allVariablesStatus = computed(() => store.getters['variable/getGroupsStatus'](groups.value));

const groups = computed(() => {
  const groups = store.getters['variable/getFlattenedGroups'](props.collection);

  // Displays root variables first
  return [
    ...groups.filter((group) => group.name === 'Root'),
    ...groups.filter((group) => group.name !== 'Root'),
  ];
});

const groupsQuantity = computed(() => groups.value.length);

const variables = computed(() => {
  return groups.value.flatMap((group) => group.variables);
});

const quantitiesDescription = computed(() => {
  const isCollectionChecked =
    store.getters['variable/getCollectionStatus'](props.collection) !== 'unselected';
  const selectedModesQuantity = isCollectionChecked
    ? props.collection.modes.filter(
        (mode) => store.getters['variable/getModeStatus'](mode.compositeId) !== 'unselected'
      ).length
    : modesQuantity.value;
  const displayModesLimit = modesQuantity.value > 1 ? 0 : 1;
  const modesDescription =
    selectedModesQuantity > displayModesLimit
      ? `${selectedModesQuantity} mode${selectedModesQuantity !== 1 ? 's' : ''}`
      : '';

  const selectedVariablesQuantity = isCollectionChecked
    ? variables.value.filter((variable) =>
        store.getters['variable/isVariableSelected'](variable.id)
      ).length
    : variables.value.length;
  const variablesDescription =
    selectedVariablesQuantity > 0
      ? `${selectedVariablesQuantity} variable${selectedVariablesQuantity !== 1 ? 's' : ''}`
      : '';

  return [modesDescription, variablesDescription].filter(Boolean).join(', ');
});

const handleAllVariablesCheck = () => {
  store.dispatch('variable/toggleGroups', groups.value);
};

const handleAllModesCheck = () => {
  store.dispatch(
    'variable/toggleModes',
    props.collection.modes.map((mode) => mode.compositeId)
  );
};

const handleCollectionCheck = () => {
  store.dispatch('variable/toggleCollection', props.collection);
};

const collectionStatus = computed(() =>
  store.getters['variable/getCollectionStatus'](props.collection)
);
</script>
